import React, { useState } from "react";
import axios from "axios";
import ApiName from "../../../ApiName";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import "./PaintDetails.css";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ShoppingBag, ArrowLeft, Ruler, Tag } from "lucide-react";

const PaintDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch painting details using React Query
  const { data: state, isLoading } = useQuery({
    queryKey: ['paintDetails', id],
    queryFn: async () => {
      const response = await axios.get(`${ApiName}/Paint/${id}`);
      const data = response.data;
      
      let images = data.images || [];
      let imageResult = [];
      
      if (images.length !== 0) {
        for (let j = 0; j < images.length; j++) {
          const fullUrl = `${ApiName}/${images[j]}`;
          imageResult.push({
            original: fullUrl,
            thumbnail: fullUrl,
            originalAlt: data.paint ? data.paint.paintName : "Paint Image",
            thumbnailAlt: data.paint ? data.paint.paintName : "Thumbnail",
          });
        }
      }
      return { images: imageResult, paint: data.paint };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Skeleton Loader matching the new layout
  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-zinc-50 pt-10 pb-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="w-24 h-4 bg-zinc-200 animate-pulse rounded mb-10"></div>
          <div className="flex flex-col lg:flex-row gap-16 xl:gap-24">
            <div className="w-full lg:w-[60%] h-[70vh] bg-zinc-200 animate-pulse rounded-md"></div>
            <div className="w-full lg:w-[40%] flex flex-col pt-4">
              <div className="w-3/4 h-10 bg-zinc-200 animate-pulse rounded mb-4"></div>
              <div className="w-1/2 h-4 bg-zinc-200 animate-pulse rounded mb-8"></div>
              <div className="w-full h-px bg-zinc-200 mb-8"></div>
              <div className="w-full h-4 bg-zinc-200 animate-pulse rounded mb-4"></div>
              <div className="w-5/6 h-4 bg-zinc-200 animate-pulse rounded mb-4"></div>
              <div className="w-4/6 h-4 bg-zinc-200 animate-pulse rounded mb-12"></div>
              <div className="w-full h-14 bg-zinc-300 animate-pulse rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback if not found
  if (!state || !state.paint) {
    return (
      <div className="w-full h-[60vh] flex flex-col items-center justify-center text-zinc-500 font-sans bg-zinc-50">
        <p className="text-xl mb-4 tracking-wider">Piece not found.</p>
        <button onClick={() => navigate(-1)} className="text-sm underline hover:text-black tracking-widest uppercase transition-colors">Return to Gallery</button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full min-h-screen bg-zinc-50 pt-8 pb-32"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        
        {/* Navigation */}
        <motion.button 
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-3 mb-10 text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-900 transition-colors group"
        >
          <ArrowLeft strokeWidth={1.5} className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back
        </motion.button>

        <div className="flex flex-col lg:flex-row gap-16 xl:gap-24 relative">
          
          {/* Left Column: Sticky Image Gallery */}
          <div className="w-full lg:w-[60%] xl:w-[65%]">
            <div className="lg:sticky lg:top-28">
              {state.images.length !== 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full bg-white p-4 shadow-sm border border-zinc-100"
                >
                  <ImageGallery
                    items={state.images}
                    additionalClass="paint-details-gallery"
                    showThumbnails={state.images.length > 1}
                    showPlayButton={false}
                    showFullscreenButton={true}
                    showNav={state.images.length > 1}
                    showBullets={false}
                    autoPlay={false}
                    slideDuration={500}
                  />
                </motion.div>
              ) : (
                <div className="w-full aspect-[4/5] bg-zinc-100 flex items-center justify-center text-zinc-400 tracking-widest uppercase text-sm border border-zinc-200">
                  Image Unavailable
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Scrolling Details */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="w-full lg:w-[40%] xl:w-[35%] flex flex-col pt-2 lg:pt-8"
          >
            {/* Title & Short Description */}
            <h1 className="text-4xl xl:text-5xl font-serif text-zinc-900 leading-tight mb-4">
              {state.paint.paintName}
            </h1>
            
            {state.paint.paintSmallDesc && (
              <p className="text-lg text-zinc-500 font-serif italic mb-8">
                {state.paint.paintSmallDesc}
              </p>
            )}

            {/* Tags */}
            {(state.paint.category || state.paint.subCategory) && (
              <div className="flex flex-wrap gap-3 mb-10">
                {state.paint.category && (
                  <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium tracking-widest uppercase text-zinc-500 bg-white border border-zinc-200 shadow-sm">
                    {state.paint.category.name}
                  </span>
                )}
                {state.paint.subCategory && (
                  <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium tracking-widest uppercase text-zinc-500 bg-white border border-zinc-200 shadow-sm">
                    {state.paint.subCategory.name}
                  </span>
                )}
              </div>
            )}

            <hr className="border-zinc-200 mb-10" />

            {/* Full Description */}
            <div className="mb-12">
              <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 flex items-center gap-2">
                <Tag size={14} /> Description
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed text-justify">
                {state.paint.paintDesc || "No description provided by the artist."}
              </p>
            </div>

            {/* Specifications */}
            <div className="mb-12">
              <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 flex items-center gap-2">
                <Ruler size={14} /> Dimensions & Specs
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap bg-white p-5 border border-zinc-100 shadow-sm">
                {state.paint.paintSpecs || "Dimensions unavailable."}
              </p>
            </div>

            {/* Sticky Purchase Action (if available) */}
            {state.paint.paintSellURL ? (
              <div className="sticky bottom-6 mt-auto">
                <motion.a
                  href={state.paint.paintSellURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.01, y: -2 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex items-center justify-between w-full p-5 bg-zinc-900 text-white hover:bg-black transition-all shadow-xl shadow-zinc-900/20 group"
                >
                  <span className="text-sm font-semibold tracking-[0.2em] uppercase">Acquire Piece</span>
                  <ShoppingBag size={18} className="transition-transform group-hover:scale-110" />
                </motion.a>
              </div>
            ) : (
              <div className="mt-auto p-5 border border-zinc-200 bg-transparent text-center">
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">
                  Not currently available for purchase
                </span>
              </div>
            )}

          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default PaintDetails;
